import { ActivityItem, DecisionStatus } from "shared/app/ActivityLog/types";
import { ApplicationStatus } from "shared/types";
import {
  Check,
  Mail01,
  ThumbsUp,
  Send01,
  CursorClick02,
  Archive,
  XClose
} from "untitledui-js-base";
import {
  CashbackIcon,
  SecurityBlockIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  MailEdit01Icon,
  Mail02Icon,
  Wallet04Icon,
  CancelCircleIcon,
  UserSettings01Icon,
  DashboardSpeed01Icon,
  Shield01Icon,
  LogoutCircle01Icon,
  CreditCardIcon,
  Pen01Icon,
  PauseIcon,
  PlayIcon,
  DiscountIcon,
  PathfinderDivideIcon,
  AlertCircleIcon,
  Note02Icon,
  File02Icon,
  Mail01Icon,
  PathfinderUniteIcon
} from "@hugeicons-pro/core-solid-rounded";

import CanadaFlag from "shared/icons/canada_flag_icon.svg?react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  IdentityCardIcon,
  PassportExpiredIcon,
  FaceIdIcon
} from "@hugeicons-pro/core-solid-rounded";
import { PassportValidIcon } from "@hugeicons-pro/core-solid-rounded";
import { useAuthContext } from "providers/Auth";
import { isUnread } from "./helpers";

export const BulletItem = ({
  activityItem,
  decisionStatus
}: {
  decisionStatus?: DecisionStatus;
  activityItem: ActivityItem;
}) => {
  const { activity_type } = activityItem;
  const { auth } = useAuthContext();
  const verificationName =
    activity_type === "report_run_manually"
      ? activityItem.details?.verification_title
      : null;

  if (
    [
      "verification_override",
      "income_override",
      "income_reverify_completed"
    ].includes(activity_type)
  )
    return (
      <div className="shrink-0 mt-4 h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center -translate-x-5">
        <HugeiconsIcon className="text-white" size={24} icon={Wallet04Icon} />
      </div>
    );

  if (activity_type === "income_reverify")
    return (
      <div className="shrink-0 mt-4 h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center -translate-x-5">
        <HugeiconsIcon
          className="text-white"
          size={24}
          icon={AlertCircleIcon}
        />
      </div>
    );

  if (activity_type === "request_retry_identity_verification")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon icon={IdentityCardIcon} className="text-content-300" />
      </div>
    );

  if (activity_type === "cancel_request_retry_identity_verification")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={PassportExpiredIcon}
          className="text-content-300"
        />
      </div>
    );

  if (activity_type === "complete_request_retry_identity_verification")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon icon={PassportValidIcon} className="text-content-300" />
      </div>
    );

  if (activity_type === "idv_data_updated")
    return (
      <div className="mt-2 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={FaceIdIcon}
          className="text-content-200"
          size={24}
        />
      </div>
    );

  if (activity_type === "failed_identity_verification_manual_override")
    return (
      <div className="mt-4 bg-danger-500 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon icon={FaceIdIcon} size={24} className="text-white" />
      </div>
    );

  if (activity_type === "role_changed")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={UserSettings01Icon}
          size={24}
          className="text-content-300"
        />
      </div>
    );

  if (activity_type === "name_changed")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={Pen01Icon}
          size={24}
          className="text-content-300"
        />
      </div>
    );

  if (activity_type === "verified_income_adjusted")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={CreditCardIcon}
          size={24}
          className="text-content-300"
        />
      </div>
    );

  if (activity_type === "canadian_criminal_report_invited")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <CanadaFlag />
      </div>
    );

  if (activity_type === "group_submitted")
    return (
      <div className="mt-4 bg-primary-500 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <CursorClick02 size="24" className="stroke-white" />
      </div>
    );

  if (activity_type === "invited")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <Send01 size="24" className="stroke-content-400" />
      </div>
    );

  if (activity_type === "resent_invite")
    return (
      <div className="mt-8 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <Send01 size="24" className="stroke-content-400" />
      </div>
    );

  if (activity_type === "decision_email_sent")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <Mail01 size="24" className="stroke-content-400" />
      </div>
    );

  if (activity_type === "credit_report_sent")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <Mail01 size="24" className="stroke-content-400" />
      </div>
    );

  if (activity_type === "report_run_manually") {
    if (verificationName === "Credit report")
      return (
        <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <HugeiconsIcon
            icon={DashboardSpeed01Icon}
            size={24}
            className="text-content-300"
          />
        </div>
      );

    if (verificationName === "Housing court report")
      return (
        <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <HugeiconsIcon
            icon={LogoutCircle01Icon}
            size={24}
            className="text-content-300"
          />
        </div>
      );

    if (verificationName?.includes("criminal"))
      return (
        <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <HugeiconsIcon
            icon={Shield01Icon}
            size={24}
            className="text-content-300"
          />
        </div>
      );
  }

  if (activity_type === "decision_made" && decisionStatus) {
    if (decisionStatus === ApplicationStatus.Approved)
      return (
        <div className="mt-4 bg-success-600 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <ThumbsUp size="24" className="stroke-success-600" fill="white" />
        </div>
      );
    if (decisionStatus === ApplicationStatus.ConditionalApproved)
      return (
        <div className="mt-4 bg-warning-500 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <ThumbsUp size="24" className="stroke-warning-500" fill="white" />
        </div>
      );
    if (decisionStatus === ApplicationStatus.Rejected)
      return (
        <div className="mt-4 bg-danger-500 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <ThumbsUp
            size="24"
            className="stroke-danger-500 rotate-180"
            fill="white"
          />
        </div>
      );

    //just to be safe if any other status appears
    return;
  }

  if (activity_type === "submitted")
    return (
      <div className="min-w-10 mt-4 bg-primary-500 h-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
          <Check size="14" className="stroke-primary-500" />
        </div>
      </div>
    );

  if (activity_type === "requested_pet_screening")
    return (
      <div className="min-w-10 mt-4 bg-white h-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
          <HugeiconsIcon
            icon={Mail01Icon}
            className="text-content-400"
            size={24}
          />
        </div>
      </div>
    );

  if (activity_type === "note")
    return (
      <div className="relative mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon icon={Note02Icon} className="text-content-400" />
        {isUnread(activityItem, auth?.id!) ? (
          <span className="absolute -top-[3px] right-0 size-3 bg-primary-500 rounded-full ring-2 ring-alpha-40" />
        ) : null}
      </div>
    );

  if (activity_type === "auto_approved")
    return (
      <div className="mt-4 bg-success-600 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <ThumbsUp size="24" className="stroke-success-600" fill="white" />
      </div>
    );

  if (["canceled", "uncanceled"].includes(activity_type))
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <Archive size="24" className="stroke-content-400" />
      </div>
    );

  if (activity_type === "remove_criminal_report")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={SecurityBlockIcon}
          size={24}
          className="fill-content-300 text-content-300"
        />
      </div>
    );

  if (["canceled", "invite_canceled"].includes(activity_type))
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={CancelCircleIcon}
          size={24}
          className="text-content-300"
        />
      </div>
    );

  if (
    [
      "landlord_survey_first_nudge",
      "landlord_survey_second_nudge",
      "landlord_survey_final_nudge",
      "landlord_survey_sent"
    ].includes(activity_type)
  )
    return (
      <div className="min-w-10 mt-4 bg-primary-500 h-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon icon={Mail02Icon} size={24} color="#ffffff" />
      </div>
    );

  if (activity_type === "landlord_survey_manual_resent")
    return (
      <div className="min-w-10 mt-4 bg-primary-500 h-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon icon={MailEdit01Icon} size={24} color="#ffffff" />
      </div>
    );

  if (activity_type === "placed_on_hold")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={PauseIcon}
          size={24}
          className="fill-content-300 text-content-300"
        />
      </div>
    );
  if (activity_type === "resumed")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={PlayIcon}
          size={24}
          className="fill-content-300 text-content-300"
        />
      </div>
    );

  if (
    activity_type === "discount_applied" ||
    activity_type === "discount_removed"
  )
    return (
      <div className="mt-4 bg-primary-500 h-10 min-w-10 border rounded-full border-primary-500 -translate-x-5 flex items-center justify-center ">
        <HugeiconsIcon icon={DiscountIcon} size={20} className="text-white" />
      </div>
    );

  if (["created_from_split", "split_application"].includes(activity_type))
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={PathfinderDivideIcon}
          size={24}
          className="fill-content-300 text-content-300"
        />
      </div>
    );

  if (activity_type === "application_merged")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={PathfinderUniteIcon}
          size={24}
          className="fill-content-300 text-content-300"
        />
      </div>
    );

  if (
    [
      "application_rent_adjusted",
      "application_rent_automatically_adjusted",
      "applicant_pet_rent_adjusted",
      "applicant_housing_assistance_adjusted"
    ].includes(activity_type)
  )
    return (
      <div className="min-w-10 mt-4 bg-white h-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={Pen01Icon}
          size={24}
          className="text-content-400"
        />
      </div>
    );

  if (
    [
      "verification_template_overridden",
      "verification_template_changed"
    ].includes(activity_type)
  )
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={File02Icon}
          size={24}
          className="fill-content-200 text-content-200"
        />
      </div>
    );

  if (activity_type === "push_applicants_to_property_management_system") {
    const pmsPushStatus =
      "pms_push_status" in activityItem.details
        ? (activityItem.details as { pms_push_status?: "success" | "failed" })
            .pms_push_status
        : undefined;

    if (pmsPushStatus === "success")
      return (
        <div className="mt-4 bg-success-600 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <Check size="24" className="stroke-white" />
        </div>
      );

    if (pmsPushStatus === "failed")
      return (
        <div className="mt-4 bg-danger-500 h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
          <XClose size="24" className="stroke-white" />
        </div>
      );

    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <XClose size="24" className="stroke-content-400" />
      </div>
    );
  }

  if (activity_type === "fee_refunded")
    return (
      <div className="mt-4 bg-white h-10 min-w-10 border rounded-full border-alpha-100 -translate-x-5 flex items-center justify-center">
        <HugeiconsIcon
          icon={CashbackIcon}
          size={24}
          className="text-content-400"
        />
      </div>
    );

  //this is a fallback just to prettify the UI if anything goes wrong
  return <div className="h-10 min-w-10" />;
};
